import "@style/Admin.scss";
import { Link } from "react-router-dom";

import { MdCheckBoxOutlineBlank, MdCheckBox } from "react-icons/md";
import { IoTrashOutline } from "react-icons/io5";
import { FaFileAlt } from "react-icons/fa";
import { Duration } from "luxon";
import { visibleCodeExpiration } from "@/utils/testTimeoutDisplayHelpers";
import Helpers from "@/Helpers";
import RightTimeApi from "@/API";
import Enums from "../enums";
import { billableRowClass, archivedRowClass, unusableCodeRowClass, testTableRowClass } from "@/utils/testRowHelpers";

function StringCell(props) {
  const hasCaption = props.caption && props.caption.length > 0;
  const hasBadge = props.badge !== undefined && props.badge !== null && props.badge > 0;
  var classes=[];
  if (props.className) 
    classes.push(props.className)
  if(hasCaption)
    classes.push("has-tooltip");
  if(hasBadge)
    classes.push("has-badge");
  // Don't add "link" class to avoid underline styling - row clicking doesn't need visual link indication
  
  const cellStyle = props.onClick ? { cursor: 'pointer' } : {};
  
  if (!props.link) {
    return (
      <td
        className={classes.join(" ")}
        title={props.caption}
        onClick={props.onClick}
        style={cellStyle}
      >
        {props.text}
        {hasBadge && <span className="badge">{props.badge}</span>}
      </td>
    );
  } else {
    return (
      <td
        className={classes.join(" ")}
        title={props.caption}
        onClick={props.onClick}
        style={cellStyle}
      >
        <Link to={props.link}>{props.text}</Link>
        {hasBadge && <span className="badge">{props.badge}</span>}
      </td>
    );
  }
}

function DeleteCell(props) {
  return (
    <td className={props.className} title={props.hoverText}>
      <IoTrashOutline />
    </td>
  );
}

function DateCell(props) {
  const title = props.isAdmin ? Helpers.EST(props.date, "MM/dd/yyyy h:mm a") : Helpers.EST(props.date, "MM/dd/yyyy");
  const date = Helpers.EST(props.date, "MM/dd/yyyy");
  if (props.onClick) {
    return (
      <td
        className={props.className}
        title={title}
        onClick={props.onClick}
        style={{ cursor: 'pointer' }}
      >
        {date ? <span>{date}</span> : null}
      </td>
    );
  } else {
    return (
      <td
        className={props.className}
        title={title}
      >
        {date ? <span>{date}</span> : null}
      </td>
    );
  }
}

function ToggleRowSelect(props) {
  return (
    <td
      data-id={props.test.id}
      className="actions"
      onClick={(e) => {
        //debugger;
        props.toggleDownload(props.test.id);
      }}
    >
      {props.checked ? (
        <MdCheckBox
          className="checkbox checked actionIcon"
          data-id={props.test.id}
        />
      ) : (
        <MdCheckBoxOutlineBlank
          className="checkbox unchecked actionIcon"
          data-id={props.test.id}
        />
      )}
    </td>
  );
}

function ReportsCell(props) {
  const hasReports = !!props.hasReports;
  if (!hasReports) {
    return <td className="center" />;
  }

  return (
    <td
      className="center"
      title="View Reports"
      onClick={props.onClick}
    >
      <FaFileAlt className="actionIcon" />
    </td>
  );
}
function displayScore(dscore, key, caption, onClick = null) {
  if (dscore === null || dscore === undefined) return <StringCell key={key} text={""} className="right" onClick={onClick} />
  if (dscore === 0) return <StringCell key={key} text={"0.00"} className="right" onClick={onClick} />
  if (dscore) {
    // dscore = dscore / 1000;
    // dscore = dscore.toFixed(2).toString();
    return <StringCell key={key} text={dscore} className="right" caption={caption} onClick={onClick} />;
  }
}

function renderColumn(name, t, props, duration) {
  const key = `${name}-${t.id}`;
  let score = null;
  let scoreMessage = null;

  // Helper function to get the appropriate click handler
  const getClickHandler = (detailTab = "") => {
    return () => {
      switch (detailTab) {
        case "matches":
          props.onMatchClick && props.onMatchClick(t.testInstanceId);
          break;
        case "scores":
          props.onScoreClick && props.onScoreClick(t.testInstanceId);
          break;
        case "":
          props.onClick && props.onClick(t.testInstanceId);
          break;
      }
    }
  };

  const hasAvailableReports =
    t.hasReports === true ||
    (typeof t.reportCount === "number" && t.reportCount > 0) ||
    (Array.isArray(t.reports) && t.reports.length > 0);

  switch (name) {
    case "Download":
      return <ToggleRowSelect key={key} test={props.test} checked={props.checked} toggleDownload={props.toggleRowSelect} />;
    case "FirstName":
      return <StringCell key={key} text={t.firstName} onClick={getClickHandler()} />;
    case "LastName":
      if (props.canViewMatches)
        return <StringCell key={key} text={t.lastName} badge={t.matches} onClick={getClickHandler("matches")} />;
      else
        return <StringCell key={key} text={t.lastName} onClick={getClickHandler()} />;
    case "CompanyName":
      return <StringCell key={key} text={t.companyName} onClick={getClickHandler()} />;
    case "StatusView":
      return <StringCell key={key} text={t.status} className="center" onClick={getClickHandler()} />;
    case "StatusEdit":
      // console.log("StatusEdit rendering - test:", t.id, "statusId:", t.statusId);
      return <td key={key}>
        <select 
          value={t.statusId||""}
          data-testid={t.id} 
          data-testinstanceid={t.testInstanceId}
          onChange={props.onSetStatus}
          className={testTableRowClass(billableRowClass(t.billable), archivedRowClass(t.archived))}
        >
          <option key={0} value=""></option>
          {props.company?.cttStatuses?.find(cts => cts.companyTestTypeId == t.companyTestTypeId)?.statuses?.map((status) => {
            return <option className={testTableRowClass(billableRowClass(status.billable), status.archive ? "archive" : "")} key={status.ctsId} value={status.ctsId}>{status.code}</option>
          })}
        </select>
        </td>;
    case "Archived":
      return (
        <td
          key={key}
          className={props.canToggleArchived ? "center actions" : "center actions readOnly"}
          data-testid={t.id}
          data-testinstanceid={t.testInstanceId}
          onClick={props.canToggleArchived ? props.onToggleArchived : undefined}
          title={
            props.canToggleArchived
              ? (t.archived ? "Unarchive test" : "Archive test")
              : "Edit Test permission required to change archive"
          }
        >
          {t.archived ? (
            <MdCheckBox className={`checkbox checked actionIcon${props.canToggleArchived ? "" : " readOnly"}`} />
          ) : (
            <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${props.canToggleArchived ? "" : " readOnly"}`} />
          )}
        </td>
      );
    case "Job":
      // Job is a Company column, should open Scores tab
      return <StringCell key={key} text={t.job} className="wrap job" onClick={getClickHandler() } />;
    case "Site":
      return <StringCell key={key} text={t.siteName} onClick={ getClickHandler() } />;
    case "Language":
      return <StringCell key={key} text={Helpers.capitalizeFirstLetter(t.language)} onClick={getClickHandler() } />;
    case "DepartmentName":
      return <StringCell key={key} text={t.departmentName} onClick={getClickHandler()} />;
    case "Supervisor":
      return <StringCell key={key} text={t.supervisor} onClick={getClickHandler()} />;
    case "Initials":
      return <StringCell key={key} text={t.initials} className="center" onClick={ getClickHandler() } />;
    case "CreateDateTime":
      return <DateCell key={key} date={t.created} className="center" testid={props.test.id} onClick={ getClickHandler() } isAdmin={props.isAdmin} />;
    case "WelcomeDateTime":
      return <DateCell key={key} date={t.welcomeTime} className="center" onClick={ getClickHandler() } isAdmin={props.isAdmin} />;
    case "LanguageTime":
      return <DateCell key={key} date={t.languageTime} className="center" onClick={getClickHandler()} isAdmin={props.isAdmin} />;
    case "CodeExpiration":
      return <DateCell key={key} date={visibleCodeExpiration(t)} className="center" onClick={ getClickHandler() } isAdmin={props.isAdmin} />;
    case "StartDateTime":
      return <DateCell key={key} date={t.started} className="center" onClick={ getClickHandler() } isAdmin={props.isAdmin} />;
    case "FinishDateTime":
      return <DateCell key={key} date={t.finished} className="center" onClick={ getClickHandler() } isAdmin={props.isAdmin} />;
    case "EmbargoDateTime":
      return <DateCell key={key} date={t.embargo} className="center" onClick={ getClickHandler() } isAdmin={props.isAdmin} />;
    case "PulledDateTime":
      return <DateCell key={key} date={t.pulled} className="center" onClick={ getClickHandler() } />;
    case "Duration":
      return <StringCell key={key} text={duration} className="center" onClick={ getClickHandler() } />;
    case "TestType":
      return <StringCell key={key} text={t.companyTestTypeName} className="center" onClick={ getClickHandler() } />;
    case "Tags":
      return <StringCell key={key} text={t.tags} className="center" onClick={ getClickHandler() } />;
    case "TestId":
      if (!t.finished && t.code)
        return (<StringCell key={key} text={t.id} className="center" onClick={ getClickHandler() } />);
      else return <StringCell key={key} text={t.id} className="center" onClick={ getClickHandler() } />;
    // case "TestDetails": return (<LinkCell key={key} text={"Details"} className="center" url={"/testInfo/"+t.id} />);
    case "TestDelete":
      return <DeleteCell key={key} hover="Delete Test" className="center" />;
    case "ApplicantScore":
      if (t.scores && t.scores.length > 0) {
        const score1 = t.scores?.find(s => s.scoreType === Enums.TEST_SCORE_TYPES.Applicant);
        if (score1 && score1.score) {
          score = score1.score;
          scoreMessage = score1.message;
        }
      }
      return displayScore(score, key, scoreMessage, getClickHandler("scores") );
    case "CompanyScore":
      if (t.scores && t.scores.length > 0) {
        const score2 = t.scores?.find(s => s.scoreType === Enums.TEST_SCORE_TYPES.Company);
        if (score2 && score2.score) {
          score = score2.score;
          scoreMessage = score2.message;
        }
      }
      return displayScore(score, key, scoreMessage, getClickHandler("scores") );
    case "OverallScore":
      if (t.scores && t.scores.length > 0) {
        const score3 = t.scores?.find(s => s.scoreType === Enums.TEST_SCORE_TYPES.Overall);
        if (score3 && score3.score) {
          score = score3.score;
          scoreMessage = score3.message;
        }
      }
      return displayScore(score, key, scoreMessage, getClickHandler("scores") );
    case "UniqueId":
      return <StringCell key={key} text={t.uniqueId} className="center" onClick={ getClickHandler() } />;
    case "Reports":
      return (
        <ReportsCell
          key={key}
          hasReports={props.canViewReports && hasAvailableReports}
          onClick={props.canViewReports ? () => props.onReportClick && props.onReportClick(t.testInstanceId) : undefined}
        />
      );
    default:
      debugger;
      break;
  }
}

export default function RecentsRow(props) {
  if (!props || !props.test) return null;
  const {columns, test, isAdmin} = props;
  
  let duration = "";
  if (test.started && test.finished) {
    if(test.finished<=test.started)
      duration = "0";
    else
      duration = Helpers.DateDiffFormat(test.started, test.finished);
  }

  return (
    <>
      {columns && columns.length > 0 ? 
        <tr key={"row-" + test.testInstanceId} className={testTableRowClass(billableRowClass(test.billable), archivedRowClass(test.archived), unusableCodeRowClass(test))} data-testid={test.id} data-testinstanceid={test.testInstanceId}>
          {columns.map((rhc) => {
            return renderColumn(rhc.name, test, props, duration);
          })}
        </tr>
        :
        <tr><td>There are no columns configured to display</td></tr>
      }
    </>
  );
}

